import { styled } from '@mui/material';

import { DocumentWrapper } from '../components/DocumentWrapper';

const StyledLastUpdated = styled('p')({
  marginTop: 0,
  fontSize: 11,
  color: 'var(--color-neutral-500)',
});

export const EULA = () => {
  return (
    <DocumentWrapper>
      <StyledLastUpdated>Last updated: June 25, 2024</StyledLastUpdated>

      <p>
        AppVelocity Inc. (“<b>AppVelocity</b>,” “<b>we</b>,” “<b>us</b>,” or “
        <b>our</b>”) welcomes you. We provide our website at{' '}
        <a href="https://www.appvelocity.io" target="_blank" rel="noreferrer">
          www.appvelocity.io
        </a>{' '}
        (including any successor site, the “<b>Site</b>”), our hosted software
        platform and other tools (including APIs), documentation and updates to
        each of them (collectively, the “<b>Services</b>”). These Terms of
        Service (this “<b>TOS</b>”) govern your access and use of the Services.
      </p>
      <p>
        Because this TOS contains legal obligations, please read this TOS
        carefully. Please print a copy of this agreement for your records. It is
        important that you read carefully and understand the terms and
        conditions of this TOS. By accessing or using any part of the Services,
        whether or not you create an account with us, you agree to be bound by
        this TOS. If you do not agree to this TOS, you will not have the right
        to access or use any part of the Services.
      </p>
      <p>
        You represent that: (a) if you are entering into this TOS on behalf of
        your employer, or any other entity or person, you are duly authorized by
        your employer, such other entity or such person to enter into this TOS
        which will be binding upon both you individually and such employer,
        other entity or person (and “you” and “your” as used in this TOS shall
        refer to such employer, entity or person), (b) you are of the legal age
        to form a binding contract with us, and (c) you are not a person barred
        from subscribing, using or accessing the Services under the laws of the
        United States or other applicable jurisdiction.
      </p>
      <ol>
        <li>
          <strong>Acceptance of TOS; Changes to TOS.</strong> The Services are
          made available by AppVelocity subject to this TOS. We reserve the right
          to update or make changes to this TOS from time to time in our sole
          discretion. Any changes will be effective immediately upon posting of
          the revised TOS and updating the “Updated” date above. If the changes
          are material, we may provide you additional notice to your email
          address, if we have one on file for you. Please return to this TOS to
          ensure familiarity with the most current version of this TOS. Your
          continued access or use of the Services after any changes to this TOS
          have been posted shall constitute your agreement to be bound by the
          then current version of this TOS in full. If you do not wish to accept
          changes to this TOS, you may discontinue your use of the Services and
          close your account with us.
        </li>
        <li>
          <strong>Services.</strong> AppVelocity provides a service which generates
          and develops code for websites, mobile apps and other digital products
          based on your input, styles and current and prior designs. The code
          generated by the Services (“
          <b>Developed Software</b>”) may be based on, or otherwise incorporate,
          open source software and subject to the applicable terms and
          conditions.
        </li>
        <li>
          <strong>Privacy Policy.</strong> Any information that you provide to
          us or give us access to (including any Materials (defined below) you
          provide to us) in using the Services is governed by our Privacy
          Policy. Please read our Privacy Policy carefully. This TOS
          incorporates by reference the terms and conditions of the Privacy
          Policy. Notwithstanding anything in our Privacy Policy or this TOS,
          you understand and agree that the Services use the Internet and third
          party networks which are not secure, and AppVelocity cannot guarantee
          that any transmission made by you in using the Services is or will be
          secure.
        </li>
        <li>
          <strong>
            Registration; Responsibility for Your Account; Materials; Third
            Party Accounts.
          </strong>
          <ol>
            <li>
              <strong>Registration.</strong> You will be required to register or
              establish an account with us in order to access all or a portion
              of the Services. You agree to (a) provide true, accurate, current
              and complete information about yourself every time you provide
              information to us, and (b) maintain and promptly update
              information about yourself to keep it true, accurate, current and
              complete. You acknowledge that, if any information provided by you
              is untrue, inaccurate, not current or incomplete, we reserve the
              right to immediately terminate this TOS, your use of the Services
              and/or your account.
            </li>
            <li>
              <strong>Responsibility for Your Account.</strong> Your account is
              for your individual use only, and not for use by any other person.
              You will be responsible for maintaining the confidentiality of
              your access credentials when accessing and using the Services and
              agree not to transfer, share or disclose your access credentials,
              or otherwise transfer, share or resell your access to the
              Services, to any third party. You agree to immediately notify
              AppVelocity of any unauthorized use of your access credentials or
              account, or any other breach of security related to your account
              or the Services. We are not liable for any loss or damage arising
              from your failure to comply with any of the foregoing obligations.
              YOU ARE ENTIRELY RESPONSIBLE FOR ALL ACTIVITIES TAKING PLACE
              THROUGH YOUR ACCOUNT, whether or not you are the individual who
              undertakes such activities.
            </li>
            <li>
              <strong>Materials.</strong> As used herein, “<b>Materials</b>”
              means designs, schematics, messages, postings, data, information,
              images, photographs, and any other materials that you post,
              upload, transmit through the use of the Services or make
              accessible to the Services. You agree that you will not post,
              upload, transmit through the use of the Services or make
              accessible to the Services any health information, payment account
              information or racial, political, religious, trade union
              membership, genetic, biometric, sexual orientation information.
              AppVelocity is not responsible or liable for the appropriateness or
              legality of any Materials that you post, upload, transmit or make
              accessible in using the Services and reserves the right to remove
              or delete to any such Materials from your account with us, at any
              time and without notice to you.
            </li>
            <li>
              <strong>Third Party Accounts.</strong> You acknowledge that in
              using the Services, you may view, access, link to, receive, and/or
              interact with and transmit Materials from and to your third party
              accounts and other sources that are not owned or controlled by
              AppVelocity (“Third Party Accounts”). You acknowledge and agree that
              (a) in using such Third Party Accounts, you will at all times
              comply with the terms and conditions of the applicable agreements
              governing your access and use of such Third Party Accounts, (b)
              AppVelocity reserves the right to discontinue and/or suspend your
              access to Third Party Accounts via the Services at any time, and
              (c) you may be required to agree to additional or different terms
              and conditions in order to have access (or have continued access)
              to your Third Party Accounts via the Services. You acknowledge and
              agree that AppVelocity does not provide, makes no warranties, and
              will have no liability with respect to (i) the Materials, products
              and services made available by third parties through your Third
              Party Accounts; (ii) the Third Party Accounts’ policies,
              practices, actions, or omissions; (iii) any Materials obtained
              from your Third Party Accounts, including the accuracy or
              thoroughness of such Materials.
            </li>
          </ol>
        </li>
        <li>
          <strong>
            Suspension, Termination and Changes to the Services and Your
            Account.
          </strong>{' '}
          We reserve the right, at any time and from time to time, temporarily
          or permanently, in whole or in part, to modify, terminate, suspend or
          discontinue the Services and/or terminate, suspend or delete your
          account, with or without notice to you. You agree that (a) AppVelocity
          shall not be liable to you or to any third party for any modification,
          termination, suspension or discontinuance of the Services, in whole or
          in part, and (b) your continued use of the Services after any changes
          to the Services have been implemented will be deemed your acceptance
          of such changes. Without limiting the generality of the foregoing, you
          specifically acknowledge that AppVelocity has the right to terminate or
          limit your account for any reason or no reason at all. Upon
          suspension, termination or deletion of your account, you will no
          longer have access to features, information, messages or other
          Materials contained within your account with us that may have value to
          you.
        </li>
        <li>
          <strong>Code of Conduct.</strong> In accessing and using the Services,
          you agree to abide by the following rules, restrictions and
          limitations:
          <ul>
            <li>
              You will not modify, adapt or alter the Services; or disassemble,
              de-compile, translate, reverse engineer or otherwise attempt to
              discover the source code or structure, sequence and organization
              of the Services (including the software of the Site), except where
              the foregoing is permitted by applicable local law notwithstanding
              such restrictions, and then only to the extent that such intended
              activities are disclosed in advance in writing to us.
            </li>
            <li>
              You will not disseminate or transmit viruses, worms, Trojan
              horses, keyboard loggers, time bombs, spyware, adware, cancelbots,
              passive collection mechanisms, or any other malicious or invasive
              code or program or upload or transmit (or attempt to upload or to
              transmit) any material that acts as a passive or active
              information collection or transmission mechanism, including,
              without limitation, clear gifts, pixels, web bugs, cookies or
              other similar devices.
            </li>
            <li>
              You will not use the Services in any manner that could damage,
              disable, overburden, impair or otherwise interfere with or disrupt
              the Services or any networks or security systems of AppVelocity or
              its service providers, or otherwise interfere with other users’
              use of the Services.
            </li>
            <li>
              You will not interfere with or circumvent any security feature or
              any feature that restricts or enforces limitations on the use of,
              or access to, the Services.
            </li>
            <li>
              You will not use any data mining software, robots, spiders or
              similar data gathering and extraction tools to retrieve, index,
              “scrape,” “data mine,” or in any way gather content or data from
              the Services or otherwise circumvent the navigational structure or
              presentation of the Services.
            </li>
            <li>
              You will not probe, scan, or test the vulnerability of any system
              or network or breach or circumvent any security or authentication
              measures of the Services.
            </li>
            <li>
              You will not remove, change or obscure any copyright, trademark
              notice, trademark, hyperlink or other proprietary rights notices
              contained within the Services or other user’s content.
            </li>
            <li>
              You will not use the Services to violate the security of any
              computer network or to transfer or store illegal material.
            </li>
            <li>
              You will not use the Services in any manner prohibited by
              applicable law, including the laws of your country if you live
              outside of the United States.
            </li>
            <li>
              You will not post or transmit any offensive Materials based on a
              person’s age, color, race, national origin, gender identity,
              sexual preference or disability; or include any unlawful, hateful,
              racist, libelous, defamatory, profane, obscene, indecent,
              pornographic, sexual or otherwise objectionable content or
              language in your Materials.
            </li>
            <li>
              You will not violate the intellectual property rights or other
              rights of another user or other third party.
            </li>
            <li>
              You will not defame, libel, ridicule, mock, disparage, threaten,
              harass, intimidate or abuse another user or other third party.
            </li>
            <li>
              You will not create or transmit unwanted electronic
              communications, such as "spam," to other users of the Services.
            </li>
            <li>You will not attempt to impersonate another person.</li>
            <li>
              You will not misrepresent information contained within your
              Materials.
            </li>
            <li>You will not disparage AppVelocity or the Services.</li>
            <li>
              You will comply with all applicable laws in your access and use of
              the Services, including the laws of your country if you live
              outside of the United States.{' '}
            </li>
          </ul>
        </li>
        <li>
          <strong>License to Access and Use Services.</strong> Subject to your
          compliance with this TOS, and solely for so long as you are permitted
          by AppVelocity to access and use the Services in its sole discretion,
          AppVelocity grants to you a limited, revocable, non-exclusive,
          non-transferable right to access the use the Services for your
          individual personal or business use only. Unless expressly granted to
          you in this TOS, AppVelocity and its licensors reserve and retain all
          rights related to the Services (including all intellectual property
          rights related thereto). You are solely liable and responsible for
          complying with all applicable laws and regulations which govern your
          use of the Services.
        </li>
        <li>
          <strong>License to Use Materials.</strong> You hereby grant to
          AppVelocity a worldwide, non-exclusive, non-transferable, royalty-free,
          fully paid-up, sublicenseable right and license to (a) use, modify,
          reproduce, and make available your Materials to you in connection with
          providing the Services and (b) use your Materials for machine learning
          purposes, including to develop, improve and provide any machine
          learning or artificial intelligence models or related technology
          (collectively, “AI Technology”). You represent and warrant that you
          are the exclusive owner of the Materials you provide to us or have all
          rights and licenses necessary to grant the rights to the Materials
          that you have granted to AppVelocity in this TOS.
        </li>
        <li>
          <strong>Ownership.</strong>
          Subject to the licenses you granted to AppVelocity in Section 8, as
          between you and AppVelocity, you shall own and retain all right, title
          and interest (including all intellectual property rights) in and to
          your Materials and the Developed Software. You understand that the
          Developed Software may be subject to applicable open source terms and
          conditions. AppVelocity (and its licensors) shall own and retains all
          right, title and interest (including all intellectual property rights)
          in and to the Services (including each component thereof), including
          all content and materials (other than your Materials) appearing
          therein and AI Technology (other than your Materials). AppVelocity shall
          also own and retain any and all metadata and aggregated data generated
          by the Services, Site and systems. All trademarks and service marks
          appearing within the Services not owned by AppVelocity are the property
          of their respective owners. The trade names, trademarks and service
          marks owned by AppVelocity, whether registered or unregistered, may not
          be used without our written consent or in any manner that is likely to
          cause confusion.
        </li>
        <li>
          <strong>Disclaimers of Warranties; Limitation of Liability.</strong>
          <ol>
            <li>
              <strong>Disclaimers.</strong> THE SERVICES AND DEVELOPED SOFTWARE
              ARE PROVIDED TO YOU “AS IS” AND YOUR USE THEREOF IS AT YOUR SOLE
              RISK. AppVelocity DOES NOT WARRANT THAT: (A) THE SERVICES (OR ANY
              MATERIALS/RESULTS OBTAINED FROM THE USE THEREOF, INCLUDING ANY
              DEVELOPED SOFTWARE) WILL BE TIMELY, ERROR-FREE, SECURE OR
              UNINTERRUPTED; (B) THE SERVICES (OR ANY MATERIALS/RESULTS OBTAINED
              FROM THE USE THEREOF, INCLUDING ANY DEVELOPED SOFTWARE) WILL MEET
              YOUR REQUIREMENTS; OR (C) ANY ERRORS OR MALFUNCTIONS IN THE
              SERVICES (OR ANY MATERIALS/RESULTS OBTAINED FROM THE USE THEREOF,
              INCLUDING ANY DEVELOPED SOFTWARE) WILL BE CORRECTED. YOU AGREE
              THAT YOU MUST EVALUATE, AND THAT YOU BEAR ALL RISKS ASSOCIATED
              WITH, THE USE OF THE SERVICES (OR ANY MATERIALS/RESULTS OBTAINED
              FROM THE USE THEREOF, INCLUDING ANY DEVELOPED SOFTWARE), INCLUDING
              WITHOUT LIMITATION, INTERRUPTIONS, DELAYS AND FAILURES THEREOF.
              AppVelocity: (I) DISCLAIMS ANY AND ALL WARRANTIES OF ANY KIND,
              EXPRESS, IMPLIED, STATUTORY OR OTHERWISE, INCLUDING THE WARRANTIES
              OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, SATISFATORY
              QUALITY, NONINFRINGEMENT, AND TITLE WITH RESPECT TO THE SERVICES
              (OR ANY MATERIALS/RESULTS OBTAINED FROM THE USE THEREOF, INCLUDING
              ANY DEVELOPED SOFTWARE), TO THE FULLEST EXTENT PERMISSIBLE UNDER
              APPLICABLE LAW, AND (II) SHALL NOT BE LIABLE IN THE EVENT OF ANY
              UNAUTHORIZED ACCESS TO OR USE OF THE SERVICES OR ANY PERSONAL
              INFORMATION AND/OR FINANCIAL INFORMATION TRANSMITTED THROUGH THE
              SERVICES, ANY INTERRUPTION, SUSPENSION OR CESSATION OF ACCESS TO
              THE SERVICES, ANY BUGS, VIRUSES, TROJAN HORSES, OR THE LIKE WHICH
              MAY BE TRANSMITTED THROUGH THE SERVICES BY ANY THIRD PARTY, AND
              ANY LOSS OR DAMAGE OF ANY KIND INCURRED AS A RESULT OF THE USE OF
              ANY SOFTWARE OR MATERIALS POSTED, DOWNLOADED, UPLOADED,
              TRANSMITTED, GENERATED BY, OR OTHERWISE MADE AVAILABLE THROUGH THE
              SERVICES.
            </li>
            <li>
              <strong>Limitation of Liability.</strong> AppVelocity WILL NOT BE
              LIABLE FOR ANY INDIRECT, INCIDENTAL, CONSEQUENTIAL, SPECIAL,
              EXEMPLARY OR PUNITIVE DAMAGES OF ANY KIND, UNDER ANY CONTRACT,
              NEGLIGENCE, STRICT LIABILITY, TORT OR OTHER THEORY, INCLUDING
              WITHOUT LIMITATION, DAMAGES FOR COST OF SUBSTITUTE PROCUREMENT,
              LOSS OF PROFITS, USE OR DATA, LOSS OF OTHER INTANGIBLES, LOSS OF
              SECURITY OF INFORMATION YOU HAVE PROVIDED IN CONNECTION WITH YOUR
              USE OF THE SERVICES OR UNAUTHORIZED INTERCEPTION OF ANY SUCH
              INFORMATION BY THIRD PARTIES, OR COST OF RECOVERY OR OTHER
              DAMAGES, EVEN IF ADVISED IN ADVANCE OF SUCH DAMAGES OR LOSSES. IN
              PARTICULAR, AND WITHOUT LIMITATION, AppVelocity WILL NOT BE LIABLE
              FOR DAMAGES OF ANY KIND RESULTING FROM YOUR USE OF OR INABILITY TO
              USE THE SERVICES OR DEVELOPED SOFTWARE, OR YOUR ABILITY TO ACCESS
              YOUR ACCOUNT, OR FROM ANY AND ALL SOFTWARE, MATERIALS, PRODUCTS
              AND SERVICES TRANSMITTED, GENERATED, ACCESSED OR OTHERWISE MADE
              AVAILABLE THROUGH THE SERVICES. YOUR SOLE AND EXCLUSIVE REMEDY FOR
              DISSATISFACTION WITH THE SERVICES OR DEVELOPED SOFTWARE IS TO STOP
              USING THE SERVICES OR DEVELOPED SOFTWARE. UNDER NO CIRCUMSTANCES
              SHALL AppVelocity BE HELD LIABLE FOR ANY DELAY OR FAILURE IN
              PERFORMANCE RESULTING DIRECTLY OR INDIRECTLY FROM ANY CAUSES
              BEYOND ITS REASONABLE CONTROL, INCLUDING, WITHOUT LIMITATION,
              INTERNET OR OTHER NETWORK (INCLUDING WITHOUT LIMITATION PHONE
              NETWORK OR OTHER TELECOMMUNICATIONS NETWORK) FAILURES OR
              “BROWNOUTS”, COMPUTER EQUIPMENT FAILURES, TELECOMMUNICATION
              EQUIPMENT FAILURES, OTHER EQUIPMENT FAILURES, ELECTRICAL POWER
              FAILURES, STRIKES, LABOR DISPUTES, RIOTS, INSURRECTIONS, CIVIL
              DISTURBANCES, SHORTAGES OF LABOR OR MATERIALS, FIRES, FLOODS,
              STORMS, EXPLOSIONS, ACTS OF GOD, WAR, GOVERNMENTAL ACTIONS, ORDERS
              OF DOMESTIC OR FOREIGN COURTS OR TRIBUNALS, NON-PERFORMANCE OF
              THIRD PARTIES, PANDEMICS, EPIDEMICS, OR LOSS OF OR FLUCTUATIONS IN
              HEAT, LIGHT, OR AIR CONDITIONING. NOTWITHSTANDING ANYTHING ELSE IN
              THIS TOS, AppVelocity’S AGGREGATE LIABILITY ARISING OUT OF OR
              RELATING IN ANY WAY TO THIS TOS AND/OR THE SERVICES, UNDER ANY
              CONTRACT, NEGLIGENCE, STRICT LIABILITY, TORT OR OTHER THEORY,
              SHALL NOT EXCEED THE FEES ACTUALLY PAID BY YOU TO AppVelocity DURING
              THE SIX (6) MONTH PERIOD PRECEDING THE DATE ON WHICH AppVelocity
              RECEIVES NOTICE OF A CLAIM FROM YOU OF US $10, WHICHEVER IS
              GREATER.
            </li>
            <li>
              <strong>Jurisdictional Limitations.</strong> SOME JURISDICTIONS DO
              NOT ALLOW LIMITATIONS ON IMPLIED WARRANTIES OR THE EXCLUSION OR
              LIMITATION OF CERTAIN DAMAGES, SO SOME OR ALL OF THE ABOVE
              DISCLAIMERS, EXCLUSIONS, OR LIMITATIONS MAY NOT APPLY TO YOU.
            </li>
          </ol>
        </li>
        <li>
          <strong>Term and Termination.</strong> This TOS is effective on the
          date you use or access any portion of the Services until it is
          terminated by you or AppVelocity. AppVelocity, in its sole discretion, may
          terminate this TOS and the Services (or any portion(s) of the
          Services), or suspend any Service for any reason, with or without
          notice to you. You may terminate this TOS by ceasing to access or use
          the Services (and any portion(s) of the Services). Upon any
          termination of this TOS, your right to access or use the Services (and
          portion(s) of the Services) will immediately cease and terminate. You
          agree that AppVelocity shall not be liable to you or any third party for
          any termination of this TOS, whether by you or AppVelocity, and any
          termination or cancellation of the Services. Upon any termination of
          this TOS, Sections 1, 2, 3, 4.2, 5, 8, 9, 10, 11, 12, 13, 15, 16, 17,
          18, 19, 20 and 21 shall survive. Upon termination of this TOS (or your
          account), you will no longer have access to your Materials,
          information, messages or other features that may have value to you
          that is associated with your account.
        </li>
        <li>
          <strong>Governing Law; Dispute Resolution.</strong>
          <ol>
            <li>
              <strong>Governing Law.</strong> This TOS and all matters arising
              out of or relating to this TOS and/or the Services will be
              governed by the internal laws of the State of California, United
              States, without giving effect to any choice of law rule that would
              cause the application of the laws of any jurisdiction other than
              the internal laws of the State of California to the rights and
              duties of AppVelocity and you. This TOS will not be governed by the
              United Nations Convention on Contracts for the International Sales
              of Goods, the application of which is expressly excluded.
            </li>
            <li>
              <strong>Good Faith Efforts to Resolve Dispute; Venue.</strong> In
              the event that any dispute arises between you and AppVelocity
              relating in any way to the Services or Developed Software, which
              may include the interpretation, construction, coverage, scope,
              performance, non-performance, breach, termination, validity or
              enforceability of this TOS (“Dispute”), you and AppVelocity will make
              bona fide efforts to settle such Dispute amicably by good faith
              consultation and discussions. If you and AppVelocity are unable to
              resolve such Dispute after good faith consultation and discussions
              for thirty (30) days, such Dispute shall be subject to the
              exclusive jurisdiction of the state and federal courts located in
              San Francisco County, in the State of California (except that
              either you or AppVelocity may seek injunctive relief in any court of
              competent jurisdiction), and the parties hereby irrevocably agree
              to submit to the personal and exclusive jurisdiction and venue of
              such courts.
            </li>
            <li>
              <strong>
                Time Limitations to Bring Claims Against AppVelocity.
              </strong>{' '}
              You must contact AppVelocity in writing within one (1) year of the
              date of the occurrence of the event or facts giving rise to a
              claim against AppVelocity, or you hereby waive the right to pursue a
              claim against AppVelocity based upon such event or facts.
            </li>
          </ol>
        </li>
        <li>
          <strong>Indemnity.</strong> You agree to defend, indemnify and hold
          harmless AppVelocity its employees, officers, directors, shareholders,
          affiliates, agents, representatives, licensors, suppliers and service
          providers, from and against all claims, losses, costs and expenses
          (including attorneys fees) arising out of (a) your and any third
          party’s use of, or activities in connection with, the Services or
          Developed Software; or (b) any violation of this TOS by you.
        </li>
        <li>
          <strong>Compliance with Applicable Law.</strong> In using the
          Services, you agree to comply with all applicable federal, state,
          local and international laws and regulations, including export and
          import laws, regulations, orders or other restrictions administered by
          the United States Commerce Department's Bureau of Export
          Administration, the United States Department of Treasury's Office of
          Foreign Assets Control or any other applicable government agency. You
          understand that use of the Services and Developed Software and
          transfer, posting and uploading of software, technology, and other
          technical data via the Services may be subject to the export and
          import laws of the United States and other countries.
        </li>
        <li>
          <strong>Notice for California Residents.</strong> Under California
          Civil Code Section 1789.3, California users are entitled to the
          following consumer rights notice: If you have a question or complaint
          regarding the Services, please send an e-mail to
          contact@appvelocity.io. California residents may reach the Complaint
          Assistance Unit of the Division of Consumer Services of the California
          Department of Consumer Affairs by mail to 1625 North Market Blvd.,
          Suite N112, Sacramento, CA 95834, or by telephone at (800) 952-5210.
        </li>
        <li>
          <strong>Entire Agreement; Amendments; Waiver.</strong> This TOS (which
          incorporates by reference our Privacy Policy) constitutes the entire
          agreement between you and AppVelocity relating to any and all access and
          use of the Services (or portion(s) thereof) and supersedes any and all
          prior or contemporaneous written or oral agreements between you and
          AppVelocity relating to the Services. No amendment or waiver of this
          Agreement will be binding on AppVelocity unless set forth in a writing
          expressly identifying this TOS and signed by an officer of AppVelocity
          and you. Either your or AppVelocity’s failure to exercise any right or
          provision of these TOS shall not constitute a waiver of such right or
          provision.
        </li>
        <li>
          <strong>Severability.</strong> If any provision of this TOS is found
          to be unlawful, void or for any reason unenforceable, that provision
          will be deemed severable from this TOS and will not affect the
          validity and enforceability of any remaining provision.
        </li>
        <li>
          <strong>Notices.</strong> You authorize AppVelocity to send you notices
          and information regarding the Services via email to the most current
          email address we have in our systems for you, and you hereby authorize
          AppVelocity to send you notices and information via email to you to the
          most current email address we have in our systems for you. You agree
          that such notice will be deemed sufficient notice and you waive any
          rights to assert failure of notice. You agree that this Agreement and
          all notices provided to you may be sent in electronic form. The
          Services may also provide notices of changes to this TOS or other
          matters by displaying such notices or by providing links to such
          notices. Without limitation, you agree that a printed version of this
          TOS and of any notice given in electronic form shall be admissible in
          arbitration, judicial or administrative proceedings based upon or
          relating to this TOS to the same extent and subject to the same
          conditions as other business documents and records originally
          generated and maintained in printed form.
        </li>
        <li>
          <strong>Assignment.</strong> You may not assign, transfer or
          sublicense any or all of your rights or obligations under this TOS
          without our express prior written consent. We may assign, transfer or
          sublicense any or all of our rights or obligations under this TOS, or
          relating to the Services, without restriction. For purposes of
          clarity, you may not transfer your account to any third party.
        </li>
        <li>
          <strong>No Third Party Beneficiaries.</strong> Nothing in this TOS
          will be construed to confer upon any third party other than the
          parties hereto any third party beneficiary right of action whatsoever.
        </li>
        <li>
          <strong>English Language; Captions and Headings.</strong> Only the
          most current English language version of this TOS is binding. In the
          event of inconsistency or discrepancy between the English language
          version and any other language version of this TOS, the most current
          English language version shall control and prevail. Any caption,
          heading or section title contained herein is inserted only as a matter
          of convenience, and in no way defines or explains any section or
          provision hereof.
        </li>
        <li>
          <strong>Contact Us.</strong> If you have any questions regarding the
          meaning of application of this TOS, please direct such questions to
          contact@appvelocity.io. Please note that e-mail communications will
          not necessarily be secure; accordingly, you should not include
          sensitive information in your e-mail correspondence with us.
        </li>
      </ol>
      <p>
        <strong>All rights reserved.</strong>
      </p>
    </DocumentWrapper>
  );
};
